import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { of, Subject } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  MasterPayrollObject,
  PayDateInfoEx,
  PaylineType,
  PayrollStartData,
  PayTypes
} from '../../../models/payroll';

// services
import {
  Deduction,
  DirectDeposit,
  Earning,
  EmployeePay,
  OpenPaylineDetailsArgs,
  Payline,
  Tax
} from '../../../models/payroll';
import { PayrollFrequency } from '../../../models/payroll-info';
import { EmployeeDirectDeposit } from '../../../models/employee-direct-deposit';
import { PayrollService } from '../../../services/payroll.service';
import { AuditService } from '../../../services/audit.service';
import { PeopleService } from '../../../services/people.service';
import { WarningLevel } from '../../../models/error-detail';
import {
  formatDatePickerValue,
  parseDatePickerValue
} from '../../../utils/datepicker-value-parser';
import { PayrollManagement } from '../../../tools/payroll-management';
import { ModalService } from '../../../services/modal.service';
import { BasePayrollComponent } from '../../../components/base/base-payroll.component';
import { ClientTimeOffRequestService } from '../../../services/time-off-request.service';
import { isBefore } from 'date-fns';
import { ToastService } from '../../../services/toast.service';
import { Store } from '@ngxs/store';
import { ClientSelectionState } from '../../../state/ClientSelectionState/ClientSelection.state';

@Component({
  selector: 'payline-detail-editor',
  templateUrl: './payline-detail-editor.component.html',
  styleUrls: ['./payline-detail-editor.component.scss'],
  standalone: false
})
export class PaylineDetailEditorComponent extends BasePayrollComponent implements OnInit {
  @Input() args: OpenPaylineDetailsArgs;

  @Input() deductionConfigs: {}; // obj lookup keyed on deductionCode
  @Input() earningConfigs: {}; // obj lookup keyed on earningid
  @Input() employeePay: EmployeePay;
  @Input() initialState: Payline;
  @Input() payroll: MasterPayrollObject;
  @Input() startData: PayrollStartData;
  @Input() taxConfigs: {}; //obj lookup keyed on taxCode

  @Output() onCloseDetails: EventEmitter<any> = new EventEmitter();
  @Output() onOpenLaborAllocations: EventEmitter<any> = new EventEmitter();

  @ViewChild('modalContent', { static: true })
  public modalContent: TemplateRef<any>;

  // expose paylineType enum for references in template
  public readonly paylineType: typeof PaylineType = PaylineType;

  // model data
  public checkStubMessage: string;
  public directDeposits: DirectDeposit[];
  public employeeDeposits: Record<number, EmployeeDirectDeposit>;
  public filteredTaxes: Tax[];
  public inactiveTaxCodes: string[];
  public frequencyOverride: number; // task 9119
  public payline: Payline;
  public orderedEarnings: Earning[];
  public orderedDeductions: Deduction[];

  // ui logic
  public hideDeposits: boolean;
  // isDirty is true if edits were made in grid.
  public isDirty: boolean = false;
  // isTouched is true if edits were made in the detail editor.
  // controls 'Cancel'/'Return To Grid' button text
  public isTouched: boolean = false;
  public disablePreview: boolean = true;
  public disableSave: boolean = true;
  public minPeriodEnd: any = {}; // 6 weeks prior to pay date
  public maxPeriodEnd: any = {}; // 6 weeks following pay date
  public paylineAllocatedByEarnings: boolean = false;
  public periodEndOverride = {};
  public personName: string;

  // tooltip
  public defaultSaveTooltip =
    'Make a change to one of the available payline fields and then click "Preview Totals" before marking this payline as OK to Pay';
  public saveTooltip: string = '';

  // clear button subjects that emit to child rows
  public clearEarningsSubject: Subject<void> = new Subject<void>();
  public clearDeductionsSubject: Subject<void> = new Subject<void>();
  public clearTaxesSubject: Subject<void> = new Subject<void>();
  public clearDepositsSubject: Subject<void> = new Subject<void>();

  public updateEarningsSubject: Subject<void> = new Subject<void>();

  get payrollFrequencies() {
    return PayrollFrequency;
  }

  get payTypes() {
    return PayTypes;
  }

  get warningLevels() {
    return WarningLevel;
  }

  public get areLaborAllocationsEnabled() {
    return Object.keys(this.payline.Allocations ?? {}).length > 0;
  }

  public get areTemporaryHourlyAllocationsSet() {
    return (
      this.areLaborAllocationsEnabled &&
      Object.values(this.payline.Allocations).some(
        alloc => Object.keys(alloc.TemporaryHourlyAllocations ?? {}).length > 0
      )
    );
  }

  public get showHoldPaymentColumn() {
    return (
      this.payline.PaylineType == PaylineType.NoCheck &&
      this.payline.Deductions.find(x => x.ThirdPartyPayeeId) != null
    );
  }

  public showLaborAllocationButton() {
    return (
      !this.args.IsReadOnly &&
      this.areLaborAllocationsEnabled &&
      this.payroll.PayType != PayTypes.Manual &&
      this.payline.PaylineType != PaylineType.NoCheck
    );
  }

  private readonly toastService = inject(ToastService);

  constructor(
    private auditService: AuditService,
    private ngModalService: NgbModal,
    private modalService: ModalService,
    private payrollMgmt: PayrollManagement,
    private payrollSvc: PayrollService,
    private peopleSvc: PeopleService,
    private timeOffService: ClientTimeOffRequestService,
    private store: Store
  ) {
    super();
  }

  ngOnInit(): void {
    this.payrollMgmt.sortPaylinesBySeq(this.payroll, this.employeePay);

    this.payline = this.employeePay.Paylines.find(line => line.PaylineId == this.args.PaylineId);

    this.personName = `${this.employeePay.EmployeeNumber} - ${this.payrollMgmt.getEmployeeFullName(
      this.employeePay
    )}`;

    this.saveTooltip = this.defaultSaveTooltip;

    // bug 9110 - exclude employee multi withholding taxes if not active
    this.peopleSvc.getEmployeeTaxes(this.payroll.ClientCode, this.args.PersonId).subscribe(resp => {
      // get all inactive taxes by tax code
      this.inactiveTaxCodes = resp.FedAndState.filter(t => !t.IsActive).map(taxCode => {
        return taxCode.TaxCode;
      });

      this._initRows();
    });

    if (this.payroll.PayType != PayTypes.Manual) {
      this._setEmployeeDirectDeposits(this.payline);
    }

    // task 8960. if payline needs to be calculated
    // enable the preview totals button on init
    this.disablePreview = !this.args.NeedsCalculation;

    // set if the payline has changes
    // calculated (IsDirty) or uncalculated (NeedsCalculation)
    this.isDirty = this.args.IsDirty || this.args.NeedsCalculation;

    // TASK 11920: default the frequency override value to that of the employee
    // when edited, write the value to the payline.PayFrequencyTaxOverride property
    this.frequencyOverride = this.payline.PayFrequencyTaxOverride || this.employeePay.PayFrequency;

    // set min/max period end override to +/- 6 weeks of pay date
    this.minPeriodEnd = formatDatePickerValue(this._addDays(new Date(this.payroll.PayDate), -42));

    this.maxPeriodEnd = formatDatePickerValue(this._addDays(new Date(this.payroll.PayDate), 42));

    if (this.payroll.PayType == PayTypes.Manual && this.payline.PeriodEndOverride == null) {
      const location = this.startData.Locations.find(x => x.Id == this.payroll.LocationId);

      let earliest: PayDateInfoEx = null;
      for (let payDate of location.NextPayDates) {
        if (earliest == null || payDate.TargetPayDate < earliest.TargetPayDate) {
          earliest = payDate;
        }
      }
      this.periodEndOverride = formatDatePickerValue(earliest.PeriodEnd);
      this.payline.PeriodEndOverride = earliest.PeriodEnd;
    } else {
      this.periodEndOverride = formatDatePickerValue(
        this.payline.PeriodEndOverride || this.payroll.Calendars[0].PeriodEnd
      );
    }

    // bug 19565. if hourly allocations, disable payline
    if (this.payline.Allocations) {
      for (let key of Object.keys(this.payline.Allocations)) {
        const alloc = this.payline.Allocations[key];
        if (
          alloc.TemporaryHourlyAllocations &&
          Object.values(alloc.TemporaryHourlyAllocations).find(
            x => +x.Hours || +x.TemporaryRateAmount || +x.PermanentRateAmount
          )
        ) {
          this.paylineAllocatedByEarnings = true;
          break;
        }
      }
    }

    this.hideDeposits =
      this.payline?.DirectDeposits?.length == 0 ||
      [PaylineType.Manual, PaylineType.Void, PaylineType.NoCheck].includes(
        this.payline?.PaylineType
      ) ||
      this.payroll.PayType == PayTypes.Manual ||
      this.payroll.PayType == PayTypes.Adjustment;

    if (this.args.OpenInModal) {
      this.ngModalService
        .open(this.modalContent, {
          size: 'xl' as 'lg',
          windowClass: 'payline-detail-modal',
          backdrop: 'static',
          keyboard: false
        })
        .result.then(
          // close
          result => this._onSave(),
          // dismiss
          () => this._onCancel()
        );
    } else {
    }

    if (this.clientCode == null) {
      this.clientCode = this.payroll.ClientCode;
    }

    this.checkStubMessage = this.payline.CheckStubMessage ?? this.payroll.Config.CheckStubMessage;
  }

  // #region Public methods

  public gotoLaborAllocations() {
    const _doGotoLaborAllocations = () => {
      this.ngModalService.dismissAll();
      this.onOpenLaborAllocations.emit({
        PersonId: this.employeePay.PersonId,
        PaylineId: this.payline.PaylineId
      });
    };

    if (this.isTouched) {
      this.modalService.confirmModalCallback.subscribe(val => {
        if (val) _doGotoLaborAllocations();
      });
      this.modalService.showConfirmModal(
        `You have unsaved changes, are you sure you want to go to Labor Allocations?`
      );
    } else {
      _doGotoLaborAllocations();
    }
  }

  public clearDeductions() {
    this._setEditedUIState();

    // the deductions need to be cleared here
    for (let ded of this.payline.Deductions) {
      ded.TemporaryAmount = 0;
      ded.TemporaryRate = 0;
    }

    // emit to child row components to clear values and inputs
    this.clearDeductionsSubject.next();
  }

  public clearDeposits() {
    this._setEditedUIState();

    // the deductions need to be cleared here
    for (let dep of this.payline.DirectDeposits) {
      dep.TemporaryAmount = 0;
      dep.CurrentAmount = 0;
    }

    // emit to child row components to clear values and inputs
    this.clearDepositsSubject.next();
  }

  public clearEarnings() {
    this._setEditedUIState();

    // the earnings need to be cleared here
    for (let earn of this.payline.Earnings) {
      earn.CurrentAmount = 0;
      earn.TemporaryRateAmount = 0;
      earn.Hours = 0;
    }

    // emit to child row components to clear values and inputs
    this.clearEarningsSubject.next();
  }

  public clearTaxes() {
    this._setEditedUIState();

    // emit to child row components to clear values and inputs
    this.clearTaxesSubject.next();
  }

  public getPaylineCount(payline: Payline) {
    return this.getVisiblePaylines(payline).length;
  }

  public getPaylineIndex(payline: Payline) {
    return this.getVisiblePaylines(payline).indexOf(payline) + 1;
  }

  public getPreviewTotals() {
    // flip 'Return to Grid' to 'Cancel'
    this.isTouched = true;

    // task 8741 - audit the button click
    this.auditService.auditPayroll(
      `payroll-payline-detail-preview-pay-clicked`,
      {
        Calendars: this.payroll.Calendars,
        ClientCode: this.payroll.ClientCode,
        PayrollType: this.payroll.PayType,
        SequenceNumber: this.payroll.SequenceNumber,
        PayDate: this.payroll.PayDate,
        LocationId: this.payroll.LocationId,
        PaylineId: this.args.PaylineId,
        PersonId: this.args.PersonId
      },
      {
        EmployeePay: JSON.stringify(this.employeePay)
      }
    );

    this._previewTotals();
  }

  public getVisiblePaylines(currentPayline: Payline) {
    return this.employeePay.Paylines.filter(
      p => p == currentPayline || !this.payrollSvc.isPaylineHidden(p)
    );
  }

  public onCheckStubMsgChange(msg: string) {
    this.payline.CheckStubMessage = msg ? msg.trim() : '';
    this._setEditedUIState();
  }

  // update our payline with the changed Deduction and toggle our state flags
  public onDeductionEdited(deduction: Deduction) {
    // replace the deduction in our payline and toggle our buttons; since we've just had
    // an edit, the 'Preview' button should now be enabled and the 'Save' button should
    // be disabled

    // get the index of our updated deduction in the payline and swap it with our new model
    let idx = this.payline.Deductions.findIndex(d => d.DeductionCode == deduction.DeductionCode);

    this.payline.Deductions[idx] = deduction;
    this._setEditedUIState();
    this.saveTooltip = this.defaultSaveTooltip;
  }

  // update our payline with the changed Direct Deposit and toggle our state flags
  public onDirectDepositEdited(directDeposit: DirectDeposit) {
    // replace the direct deposit in our payline and toggle our buttons; since we've just had
    // an edit, the 'Preview' button should now be enabled and the 'Save' button should
    // be disabled

    // get the index of our updated direct deposit in the payline and swap it with our new model
    let idx = this.payline.DirectDeposits.findIndex(
      d => d.DirectDepositId == directDeposit.DirectDepositId
    );

    this.payline.DirectDeposits[idx] = { ...directDeposit };
    this._setEditedUIState();
    this.saveTooltip = this.defaultSaveTooltip;
  }

  // update our payline with the changed Earning and toggle our state flags
  public onEarningEdited(earning: Earning) {
    // replace the earning in our payline and toggle our buttons; since we've just had
    // an edit, the 'Preview' button should now be enabled and the 'Save' button should
    // be disabled

    // get the index of our updated earning in the payline and swap it with our new model
    let idx = this.payline.Earnings.findIndex(e => e.EarningId == earning.EarningId);

    this.payline.Earnings[idx] = { ...earning };
    this._setEditedUIState();
    this.saveTooltip = this.defaultSaveTooltip;
  }

  public onFreqOverrideChange() {
    this.payline.PayFrequencyTaxOverride = this.frequencyOverride || null;
    this._setEditedUIState();
  }

  public onPeriodEndOverrideChange() {
    this.payline.PeriodEndOverride = this.periodEndOverride
      ? parseDatePickerValue(this.periodEndOverride)
      : null;
    this.store.selectOnce(ClientSelectionState.canEnableTimeOff).subscribe(result => {
      if (result) {
        let earliestDate: Date;
        this.payroll.Calendars.forEach(c => {
          if (earliestDate == null || isBefore(c.PeriodBegin, earliestDate)) {
            earliestDate = c.PeriodBegin;
          }
        });
        this.timeOffService
          .getRequestsForPayroll(
            this.clientCode,
            this.args.PersonId,
            earliestDate,
            this.payline.PeriodEndOverride
          )
          .subscribe(requests => {
            const hoursMap = new Map<string, number>();
            requests.forEach(record => {
              let current = 0;
              if (hoursMap.has(record.EarningId)) {
                current = hoursMap.get(record.EarningId);
              }
              record.Days.forEach(day => {
                current += day.Hours;
              });
              hoursMap.set(record.EarningId, current);
            });
            this.payline.Earnings.forEach(earning => {
              if (hoursMap.has(earning.EarningId)) {
                const hours = hoursMap.get(earning.EarningId);
                earning.Hours = hours;
                const oEarn = this.orderedEarnings.find(x => x.EarningId == earning.EarningId);
                oEarn.Hours = hours;
              }
            });
            this.updateEarningsSubject.next();
          });
      }
    });
    this._setEditedUIState();
  }

  // update our payline with the changed tax and toggle our state flags
  public onTaxEdited(tax: Tax) {
    // replace the tax in our payline and toggle our buttons; since we've just had
    // an edit, the 'Preview' button should now be enabled and the 'Save' button should
    // be disabled

    // get the index of our updated tax in the payline and swap it with our new model
    let idx = this.payline.Taxes.findIndex(t => t.TaxCode == tax.TaxCode);

    this.payline.Taxes[idx] = { ...tax };
    this._setEditedUIState();
    this.saveTooltip = this.defaultSaveTooltip;
  }

  public restoreDefaults() {
    this.isDirty = false;

    let payline = this.employeePay.Paylines.find(line => line.PaylineId == this.args.PaylineId);

    const idx = this.employeePay.Paylines.indexOf(payline);

    const initalState = JSON.parse(JSON.stringify(this.initialState));
    initalState.Allocations = this.employeePay.Paylines[idx].Allocations;

    this.employeePay.Paylines[idx] = JSON.parse(JSON.stringify(initalState));
    this.payline = JSON.parse(JSON.stringify(initalState));

    this.frequencyOverride = this.payline.PayFrequencyTaxOverride || this.employeePay.PayFrequency;
    this.periodEndOverride = formatDatePickerValue(this.payroll.Calendars[0].PeriodEnd);

    this._initRows();

    // FIXME: validate the assumptions made by this comment.  We've now learned that the Agile API
    // actually DOES calculate totals for OkToPay paylines when initializing the payroll, so we might
    // actually have calculated totals    --nk 2022-10-24
    // we pulled the initial state from the PayrollShadow table
    // so it did not include any calculated totals. run a preview call now
    this._previewTotals();

    this.disableSave = true;
    this.isTouched = true; // "Cancel/Return To Grid" button
    this.saveTooltip = this.defaultSaveTooltip;

    // bug 19565. do not display default earning values if this payline has earning allocations
    // continue to display the summed allocation values the same as when the editor opened
    if (this.paylineAllocatedByEarnings) {
      this.toastService.show(
        'Earnings cannot be restored, to edit click View/Edit Allocations button.',
        'bg-warning'
      );
    }

    this.toastService.show('Defaults restored');
  }

  public doSave() {
    this._onSave();
  }

  // #endregion

  // #region Private methods

  private _addDays(date, days) {
    var result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  private _initRows() {
    this._setOrderedDeductions();
    this._setOrderedEarnings();
    this._setFilteredTaxes();
    this._setDirectDeposits();
  }

  private _onCalculationsReceived(response: Payline[]): void {
    // pass the calculated employee pay object back to the grid
    this.employeePay.Paylines = response;

    this.onCloseDetails.emit({
      pay: this.employeePay,
      line: this.payline,
      isDirty: this.isDirty
    });
  }

  private _onCancel() {
    // task 8743 - record an detail edit cancel audit event
    this.auditService.auditPayroll(`payroll-payline-detail-edit-cancelled`, {
      Calendars: this.payroll.Calendars,
      ClientCode: this.payroll.ClientCode,
      SequenceNumber: this.payroll.SequenceNumber,
      PayrollType: this.payroll.PayType,
      PayDate: this.payroll.PayDate,
      LocationId: this.payroll.LocationId,
      PaylineId: this.args.PaylineId,
      PersonId: this.args.PersonId
    });

    this.onCloseDetails.emit(null);
  }

  private _onPreviewReceived(response: Payline[]): void {
    // pass the returned paylines into our model and update our payline view model.
    // pass any errors/warnings to the UI for the user.  if we have any critical
    // errors, keep the Save button disabled
    this.employeePay.Paylines = response;

    this.payline = this.employeePay.Paylines.find(line => line.PaylineId == this.args.PaylineId);

    // update necessary fields from the preview call,
    // such as CurrentAmounts (Totals), and some earning rates
    this._updateRows();

    // any crits?  don't enable Save
    this.disableSave = (this.payline.Errors ?? []).some(
      e => e.WarningLevel == WarningLevel.Critical
    );

    this.saveTooltip = this.disableSave
      ? 'Please resolve critical errors before marking this payline as OK to Pay.'
      : '';
    this.disablePreview = true;
  }

  private _onSave() {
    // task 8744 - audit the button click
    this.auditService.auditPayroll(`payroll-payline-detail-saved`, {
      Calendars: this.payroll.Calendars,
      ClientCode: this.payroll.ClientCode,
      PayrollType: this.payroll.PayType,
      SequenceNumber: this.payroll.SequenceNumber,
      PayDate: this.payroll.PayDate,
      LocationId: this.payroll.LocationId,
      PaylineId: this.args.PaylineId,
      PersonId: this.args.PersonId
    });

    // post the user's changes to the api for updated calculations.  the callback
    // handler will take care of the rest
    this.payrollSvc
      .calculatePaylines(
        this.payroll.ClientCode,
        this.employeePay.PersonId,
        this.payroll.Year,
        this.payroll.SequenceNumber,
        this.employeePay.Paylines,
        false,
        true
      )
      .pipe(
        catchError(err => {
          // calculate payline will return a 400 if there were critical errors on any paylines
          // just pass that along to the subscription
          return of(err);
        })
      )
      .subscribe(resp => {
        if (resp) {
          this._onCalculationsReceived(resp);
        }
      });
  }

  // this function is called after Preview Totals
  // and Restore Defaults is clicked
  private _previewTotals() {
    // NOTE: we need to set OkToPay to true for the object we pass along
    // in order to get totals back from the calculate payline request.
    // we don't want to change the underlying model yet because the user
    // can still cancel these changes
    const paylines = JSON.parse(JSON.stringify(this.employeePay.Paylines));

    // Bug 9577: send only the active payline being edited as OkToPay
    for (let pay of paylines) {
      if (pay.PaylineId == this.payline.PaylineId) {
        pay.OkToPay = true;
      }
    }

    // issue a preview-only calculation request and update our employeePay with the
    // response model.  if we get any critical errors, leave the Save button disabled.
    // users can't apply edits that would result in crit errors.
    this.payrollSvc
      .calculatePaylines(
        this.payroll.ClientCode,
        this.employeePay.PersonId,
        this.payroll.Year,
        this.payroll.SequenceNumber,
        paylines,
        true,
        true
      )
      .pipe(
        catchError(err => {
          // calculate payline will return a 400 if there were critical errors on any paylines
          // just pass that along to the subscription
          return of(err);
        })
      )
      .subscribe(
        resp => this._onPreviewReceived(resp)
        // err => this._onCalculationError(err)
      );
  }

  private _setDirectDeposits() {
    const deposits = this.payline.DirectDeposits;
    this.directDeposits = JSON.parse(JSON.stringify(deposits));
  }

  private _setEditedUIState() {
    this.isDirty = true;
    this.disablePreview = false;
    this.disableSave = true;
    this.isTouched = true; // "Cancel/Return To Grid" button
  }

  private _setEmployeeDirectDeposits(payline: Payline) {
    this.peopleSvc
      .getDirectDeposits(this.payroll.ClientCode, this.args.PersonId)
      .subscribe(resp => {
        // task 8937 - fetch the corresponding EmployeeDirectDeposit models
        // loop through each direct deposit on the payline
        // and match it with the employee direct deposit
        this.employeeDeposits = {};

        this.payline.DirectDeposits.forEach(d => {
          this.employeeDeposits[d.DirectDepositId] = resp.find(ed => ed.Id == d.DirectDepositId);
        });
      });
  }

  private _setOrderedDeductions() {
    const deductions = this.payline.Deductions.sort(
      (a, b) =>
        this.deductionConfigs[a.DeductionCode].AppearanceSequence -
        this.deductionConfigs[b.DeductionCode].AppearanceSequence
    );

    this.orderedDeductions = JSON.parse(JSON.stringify(deductions));
  }

  private _setOrderedEarnings() {
    const earnings = this.payline.Earnings.filter(
      earning => !this.earningConfigs[earning.EarningId].IsThirdPartySick
    ).sort(
      (a, b) =>
        this.earningConfigs[a.EarningId].AppearanceSequence -
        this.earningConfigs[b.EarningId].AppearanceSequence
    );

    this.orderedEarnings = JSON.parse(JSON.stringify(earnings));
  }

  private _setFilteredTaxes() {
    const taxes = this.payline.Taxes.map(tax => {
      // work around odd issue where tax code randomly comes back from
      // the API with trailing whitespace.
      tax.TaxCode = tax.TaxCode.trim();
      return tax;
    }).filter(tax => tax.TaxPaidBy !== 'C' && !this.inactiveTaxCodes.includes(tax.TaxCode.trim()));

    this.filteredTaxes = JSON.parse(JSON.stringify(taxes));
  }

  private _updateRows() {
    for (let earn of this.payline.Earnings) {
      for (let oEarn of this.orderedEarnings) {
        if (oEarn.EarningId != earn.EarningId) {
          continue;
        }

        oEarn.PermanentRateAmount = earn.PermanentRateAmount;
        oEarn.CurrentAmount = earn.CurrentAmount;
      }
    }

    for (let ded of this.payline.Deductions) {
      for (let oDed of this.orderedDeductions) {
        if (oDed.DeductionCode != ded.DeductionCode) {
          continue;
        }

        oDed.CurrentAmount = ded.CurrentAmount;
      }
    }

    for (let tax of this.payline.Taxes) {
      for (let fTax of this.filteredTaxes) {
        if (fTax.TaxCode != tax.TaxCode) {
          continue;
        }

        fTax.CurrentAmount = tax.CurrentAmount;
      }
    }

    for (let deposit of this.payline.DirectDeposits) {
      for (let dep of this.directDeposits) {
        if (dep.DirectDepositId != deposit.DirectDepositId) {
          continue;
        }

        dep.CurrentAmount = deposit.CurrentAmount;
      }
    }
  }

  // #endregion
}
